import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines } from "@/components/site/Reveal";

const HERO_BG =
  "https://images.unsplash.com/photo-1425913397330-cf8af2ff40a1?w=2000&q=85&auto=format&fit=crop";

const bars = [0.4, 0.9, 0.55, 1, 0.5, 0.8, 0.65, 0.95, 0.45, 0.75];
const Equalizer = ({ className = "" }) => (
  <div className={`flex items-end gap-1.5 ${className}`} aria-hidden="true">
    {bars.map((h, i) => (
      <motion.span
        key={i}
        className="w-1 rounded-full bg-manor-rust"
        style={{ height: 44, originY: 1 }}
        initial={{ scaleY: 0.2 }}
        animate={{ scaleY: [0.2, h, 0.35, h * 0.7, 0.2] }}
        transition={{
          duration: 1.1 + (i % 3) * 0.35,
          repeat: Infinity,
          ease: "easeInOut",
          delay: i * 0.08,
        }}
      />
    ))}
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Misty pine forest in the Dehradun foothills"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-manor-bg"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-manor-bg via-manor-bg/20 to-manor-bg/30" />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-20 md:px-10 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="mb-8 flex items-center gap-5"
        >
          <span className="h-px w-12 bg-manor-rust" />
          <span className="font-mono text-xs tracking-[0.28em] uppercase text-manor-text/80">
            A TMH Circle Retreat · 24–27 Sept · The Manor House, Dehradun
          </span>
          <Equalizer className="ml-2 hidden sm:flex" />
        </motion.div>

        <MaskLines
          lines={["Heal With", "Music"]}
          startDelay={0.25}
          className="font-display text-[19vw] uppercase leading-[0.82] tracking-tight text-manor-text md:text-[13vw]"
          lineClassName="font-semibold"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-8 flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-xl font-display text-2xl font-light italic leading-snug text-manor-text/90 md:text-3xl">
            Where sound becomes medicine — three days deep in the forests of Dehradun.
          </p>
          <p className="max-w-sm text-sm font-light leading-relaxed text-manor-text/70 md:text-right">
            Live sets, sound baths and long, unhurried nights — a mindful reset held by
            TMH Circle at The Manor House.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] uppercase text-manor-text/60"
      >
        Scroll ↓
      </motion.div>
    </section>
  );
};

export default Hero;
